// src/hooks/mediaInsights/useFetchNews.ts

import { useQuery } from "@tanstack/react-query";
import { newsQueryOptions } from "@/services/queries/mediaInsights/newsQueries";
import type { FetchNewsParams, News } from "@/types/mediaInsights/news";

export interface UseFetchNewsOptions extends FetchNewsParams {
  enabled?: boolean;
}

export interface UseFetchNewsResult {
  news: News[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isInitialLoading: boolean;
  hasData: boolean;
  refetch: () => void;
}

/**
 * Custom hook for fetching news with optional filters
 *
 * @param options - Filter options for news
 * @param options.limit - Maximum number of news articles to return
 * @param options.offset - Number of news articles to skip before returning results
 * @param options.min_alpha_grade - Minimum alpha grade value (0-100)
 * @param options.max_alpha_grade - Maximum alpha grade value (0-100)
 * @param options.start_datetime - Return news created at or after this datetime (ISO 8601)
 * @param options.end_datetime - Return news created at or before this datetime (ISO 8601)
 * @param options.enabled - Whether the query should run automatically (default: true)
 *
 * @example
 * ```tsx
 * const { news, isLoading, error } = useFetchNews({
 *   limit: 50,
 *   start_datetime: '2024-01-01T00:00:00Z',
 *   min_alpha_grade: 50,
 *   max_alpha_grade: 100
 * });
 * ```
 */
export function useFetchNews(
  options: UseFetchNewsOptions = {}
): UseFetchNewsResult {
  const { enabled = true, ...params } = options;

  const query = useQuery({
    ...newsQueryOptions(params),
    enabled,
  });

  return {
    news: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isInitialLoading: query.isLoading && query.fetchStatus === "fetching",
    hasData: !!query.data?.length,
    refetch: query.refetch,
  };
}
